import { Link, Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { api, ApiError, call } from '../../api/client';
import { useSession } from '../../api/session';
import { Button, Card, ErrorText, Field, Input } from '../../components/ui';
import { ProviderButtons, signInErrorText, useSignInOptions } from './SignInOptions';
import { takeHashToken } from '../../lib/hash-token';
import { Logo } from '../../components/Logo';

export function AuthFrame({ title, children, footer }: { title: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2">
          <Logo size={56} />
          <p className="text-lg font-semibold text-[var(--accent)]">FamilyChores</p>
        </div>
        <Card className="p-6">
          <h1 className="mb-5 text-xl font-semibold">{title}</h1>
          {children}
        </Card>
        {footer ? <div className="mt-4 text-center text-sm text-[var(--muted)]">{footer}</div> : null}
      </div>
    </div>
  );
}

function fieldError(err: unknown, field: string): string | undefined {
  return err instanceof ApiError ? err.problem.errors?.find((e) => e.field === field)?.message : undefined;
}

export function LoginPage() {
  const { status, login } = useSession();
  const { redirect, error: providerError } = useSearch({ from: '/login' });
  const options = useSignInOptions().data;
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') return <Navigate to={(redirect as '/') ?? '/'} />;

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await login(String(form.get('email')), String(form.get('password')));
      await navigate({ to: (redirect as '/') ?? '/' });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame
      title="Sign in"
      footer={options?.password ? <>New here? <Link to="/register" className="text-[var(--accent)] hover:underline">Create an account</Link></> : <>New here? Continuing with Google or Microsoft creates your account.</>}
    >
      {providerError ? <p role="alert" className="mb-4 rounded-xl bg-amber-100 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">{signInErrorText(providerError)}</p> : null}
      <ProviderButtons returnTo={redirect} />
      {options?.password ? (
      <>
      {options.google || options.microsoft ? <p className="my-4 text-center text-xs text-[var(--muted)]">or, on this test server, with email and password</p> : null}
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        <Field label="Email">{(id) => <Input id={id} name="email" type="email" autoComplete="email" required />}</Field>
        <Field label="Password">{(id) => <Input id={id} name="password" type="password" autoComplete="current-password" required />}</Field>
        <ErrorText error={error} />
        <Button type="submit" className="w-full" busy={busy}>
          Sign in
        </Button>
        <Link to="/forgot-password" className="block text-center text-sm text-[var(--muted)] hover:underline">
          Forgot your password?
        </Link>
      </form>
      </>
      ) : null}
    </AuthFrame>
  );
}

export function RegisterPage() {
  const options = useSignInOptions().data;
  // Without passwords (production), accounts are created by continuing with Google or Microsoft.
  if (options && !options.password) return <Navigate to="/login" />;
  return <PasswordRegisterPage />;
}

function PasswordRegisterPage() {
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const email = String(f.get('email'));
      await call(api.POST('/api/v1/auth/register', { body: { email, password: String(f.get('password')), displayName: String(f.get('displayName')) } }));
      setDone(email);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthFrame title="Check your email" footer={<Link to="/login" className="text-[var(--accent)] hover:underline">Back to sign in</Link>}>
        <p className="text-sm text-[var(--muted)]">
          We sent a confirmation link to <strong className="text-[var(--text)]">{done}</strong>. Open it to finish setting up your account.
        </p>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame title="Create your account" footer={<>Already have an account? <Link to="/login" className="text-[var(--accent)] hover:underline">Sign in</Link></>}>
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        <Field label="Your name" error={fieldError(error, 'displayName')}>{(id) => <Input id={id} name="displayName" required maxLength={80} autoComplete="name" />}</Field>
        <Field label="Email" error={fieldError(error, 'email')}>{(id) => <Input id={id} name="email" type="email" required autoComplete="email" />}</Field>
        <Field label="Password" error={fieldError(error, 'password')} hint="At least 12 characters. A short sentence works well.">
          {(id) => <Input id={id} name="password" type="password" required minLength={12} autoComplete="new-password" />}
        </Field>
        {error && !(error instanceof ApiError && error.code === 'VALIDATION_FAILED') ? <ErrorText error={error} /> : null}
        <Button type="submit" className="w-full" busy={busy}>
          Create account
        </Button>
      </form>
    </AuthFrame>
  );
}

export function VerifyEmailPage() {
  const { status, reloadMe } = useSession();
  const [token] = useState(() => takeHashToken());
  const [state, setState] = useState<'working' | 'ok' | 'failed'>(token ? 'working' : 'failed');
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !token) return;
    started.current = true;
    void call(api.POST('/api/v1/auth/verify-email', { body: { token } }))
      .then(async () => {
        setState('ok');
        if (status === 'authenticated') await reloadMe();
      })
      .catch(() => setState('failed'));
  }, [token, status, reloadMe]);
  return (
    <AuthFrame title={state === 'ok' ? 'Email confirmed' : state === 'failed' ? 'Link expired' : 'Confirming…'}>
      {state === 'ok' ? (
        <Link to="/"><Button className="w-full">Continue</Button></Link>
      ) : state === 'failed' ? (
        <p className="text-sm text-[var(--muted)]">This link is invalid or has expired. Sign in and request a new one.</p>
      ) : null}
    </AuthFrame>
  );
}

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    try {
      await call(api.POST('/api/v1/auth/password/forgot', { body: { email: String(new FormData(e.currentTarget).get('email')) } }));
    } finally {
      setBusy(false);
      setSent(true); // same message either way (no account enumeration)
    }
  };
  return (
    <AuthFrame title="Reset your password" footer={<Link to="/login" className="text-[var(--accent)] hover:underline">Back to sign in</Link>}>
      {sent ? (
        <p className="text-sm text-[var(--muted)]">If that email has an account, a reset link is on its way. It expires in 30 minutes.</p>
      ) : (
        <form className="space-y-4" onSubmit={(e) => void submit(e)}>
          <Field label="Email">{(id) => <Input id={id} name="email" type="email" required autoComplete="email" />}</Field>
          <Button type="submit" className="w-full" busy={busy}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}

export function ResetPasswordPage() {
  const [token] = useState(() => takeHashToken());
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await call(api.POST('/api/v1/auth/password/reset', { body: { token, newPassword: String(new FormData(e.currentTarget).get('password')) } }));
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  if (!token) return <AuthFrame title="Link expired"><p className="text-sm text-[var(--muted)]">Request a new reset link.</p></AuthFrame>;
  return (
    <AuthFrame title={done ? 'Password changed' : 'Choose a new password'}>
      {done ? (
        <>
          <p className="mb-4 text-sm text-[var(--muted)]">All your devices were signed out. Sign in with your new password.</p>
          <Link to="/login" search={{ redirect: undefined }}><Button className="w-full">Sign in</Button></Link>
        </>
      ) : (
        <form className="space-y-4" onSubmit={(e) => void submit(e)}>
          <Field label="New password" error={fieldError(error, 'password')} hint="At least 12 characters.">
            {(id) => <Input id={id} name="password" type="password" required minLength={12} autoComplete="new-password" />}
          </Field>
          {error && !(error instanceof ApiError && error.code === 'VALIDATION_FAILED') ? <ErrorText error={error} /> : null}
          <Button type="submit" className="w-full" busy={busy}>
            Save password
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}
